import {
  GraphComparisonApiService,
  GraphLegacyApiService,
  IProduct,
  Review,
} from '@kizi/comparator-core';
import { Div, P, Span } from '@kizi/core-components';
import { Anchor, IconV2, withTooltip } from '@kizi/shared-components';
import React from 'react';
import { Field } from '../components/Product';
import {
  PriceCapCeillingLabel,
  getElectricityRates,
  getGasRates,
} from '../components/Product/ExpandedPart/Rates';
import { Group } from '../components/ProductSummary';
import { COMPARATOR_TYPE } from '../funnels';
import { formatPrice, scrollToTop } from '../utils/global';
import { getLabel } from './translations';

export type PriceMode = 'month' | 'year';

interface Clause {
  clauseExplanation: string;
  clauseName: string;
  clauseNumber: string;
}

interface ConditionConditionDetail {
  documents: Document[];
  conditionNumber: string;
  conditionText: string;
}

interface Document {
  attachmentDescription: string;
  attachmentType: string;
  urlAttachment: string;
}

interface Coverage {
  sumInsured: number | null;
  totalInstallmentAmount: number | null;
  deductibleAmount: number | null;
  deductibleDescription?: string | null;
  discountsInstallmentAmount: number | null;
  grossPremiumInstallment: number | null;
  insuranceTaxInstallmentAmount: number | null;
  surchargesInstallmentAmount: number | null;
  netPremiumInstallment: number | null;
  effectiveDate: string;
  productDescription: string;
  clauses: Clause[] | null;
  documents: Document[] | null;
  conditionConditionDetails?: ConditionConditionDetail[] | null;
}

export interface EnergyLabel {
  co2_per_kwh: number;
  percentage_bio: number;
  percentage_gas: number;
  percentage_groen: number;
  percentage_kern: number;
  percentage_kolen: number;
  percentage_overig: number;
  percentage_water: number;
  percentage_wind: number;
  percentage_zon: number;
  radioactief_afval_per_kwh: number;
}

export interface Policy {
  contractDurationInMonths: number | null;
  contractNumber: string;
  productCode: string;
  productDescription: string;
  paymentTermInMonths: number;
  porCompany: string | null;
  companyNameDescription: string;
  coverageBuildings: Coverage[] | null;
  coverageHomes: Coverage[] | null;
  coverageLiabilities: Coverage[] | null;
}

export interface Provider {
  id: string;
  name: string;
  logo: string;
  description?: string;
  attributes?: ProviderAttributes;
}

type MoveWindow = {
  minDays?: number | string;
  maxDays?: number | string;
};

interface AnnualAccount {
  digital: string;
  mail: string;
}
interface OnlinePhoneApp {
  online: string;
  phone: string;
  app: string;
}

interface PaymentMethod {
  automatic: string;
  iDeal: string;
  acceptgiro: string;
}

export interface ProsAndConsItem {
  text1?: string;
  text2?: string;
  title1: string;
  title2: string;
}

export interface ProsAndConsProps {
  pros?: ProsAndConsItem | string[];
  cons?: ProsAndConsItem | string[];
}

export interface ProviderAttributes {
  postcoderoosregeling?: string;
  annual_account?: AnnualAccount;
  edit_amount?: OnlinePhoneApp;
  meter_readings?: OnlinePhoneApp;
  payment_method?: PaymentMethod;
  usage?: OnlinePhoneApp;
  prosAndCons?: ProsAndConsProps | ProsAndConsProps[] | null;
  switchCountWeek?: number;
  reviews?: {
    rs_price_understanding_total?: string;
    rs_payment_total?: string;
    rs_transfer_process_total?: string;
    rs_online_service_total?: string;
    rs_customer_service_total?: string;
    rs_durability_total?: string;
    total_score?: string;
    expert_quote?: string;
    good_indicator?: string[];
    bad_indicator?: string[];
    helpdesk_score?: string;
    customer_score?: string;
    rating?: string;
  };
  noReflectionPeriod?: boolean;
  switchWindow?: {
    minDays?: number | string;
    maxDays?: number | string;
  };
  moveWindow?: MoveWindow | MoveWindow[];
  extraInfo?: any[];
  sustainabilityScore?: string | null;
  sustainabilityCommerceScore?: string | null;
  sustainabilityInvestmentScore?: string | null;
}

interface UnderwritingCompanySpecific {
  underwritingResult: string;
  rejectionReason: string[];
}
export interface Usp {
  type: string;
  content: string;
}

export interface Usps {
  [key: string]: Usp;
}
export interface HomeProduct {
  effectiveDate: string | null;
  contractNumber: string | null;
  porCompany: string | null;
  productCode: string | null;
  productDescription: string | null;
  companyNameDescription: string | null;
  costsInstallmentAmount: number | null;
  grossPremiumInstallment: number | null;
  insuranceTaxInstallmentAmount: number | null;
  insuranceTaxPercentage: number | null;
  netPremiumInstallment: number | null;
  netPremiumInstallmentExcludingPackageDiscount: number | null;
  numberOfSubPolicies: number | null;
  offerNumber: string | null;
  packageDiscountInstallmentAmount: number | null;
  packageDiscountPercentage: number | null;
  paymentTermInMonths: number | null;
  surchargesInstallmentAmount: number | null;
  totalInstallmentAmount: number | null;
  installmentAmountRenewalCharges: number | null;
  oneOffCostsAmount: number | null;
  externalSourceName: string | null;
  customProperties: {
    supplierKrn: string;
  };
  policyPolicyDetails: Policy[];
  underwritingCompanySpecifics: UnderwritingCompanySpecific[];
  type?: COMPARATOR_TYPE;
  compare?: boolean;
}

export interface Attributes {
  annual_account: {
    digital: string;
    mail: string;
  };
  edit_amount: {
    app: string;
    online: string;
    phone: string;
  };
  extraInfo: string[];
  meter_readings: {
    app: string;
    online: string;
    phone: string;
  };
  moveWindow: {
    maxDays: number;
    minDays: number;
  };
  noReflectionPeriod: boolean;
  payment_method: {
    acceptgiro: string;
    automatic: string;
    iDeal: string;
  };
  postcoderoosregeling: string;
  prosAndCons: {
    pros: ProsConsItem;
    cons: ProsConsItem;
  };
  reviews: {
    bad_indicator: string[];
    customer_score: string;
    expert_quote: string;
    good_indicator: string[];
    helpdesk_score: string;
    rs_customer_service_total: string;
    rs_durability_total: string;
    rs_online_service_total: string;
    rs_payment_total: string;
    rs_price_understanding_total: string;
    rs_transfer_process_total: string;
    total_score: string;
  };
  sustainabilityScore: string;
  switchCountWeek: number;
  switchWindow: {
    maxDays: number;
    minDays: number;
  };
  usage: {
    app: string;
    online: string;
    phone: string;
  };
}

export interface AttributesEmpty {
  contractEndDate: string | null;
  tags: string[];
}

export interface PriceGroup {
  total: number;
  totalTax?: number;
  discountIncluded: number;
  taxRate?: number;
}

export interface Price {
  breakdown: any;
  year: PriceGroup;
  month: PriceGroup;
}

export interface EnergyProduct {
  id: string;
  name: string;
  description: string;
  category: string;
  type?: COMPARATOR_TYPE | string;
  position: number;
  image: string;
  availability: any;
  energy_label?: EnergyLabel;
  usps?: Usps;
  products: Partial<EnergyProduct>[];
  filters: any;
  featured: any;
  sorting: any;
  features: Array<any>;
  links: Array<any>;
  attributes: Attributes | AttributesEmpty;
  price: Price;
  provider: Provider;
  rating: {
    score: number;
  };
  electricitysupplyhigh?: number;
  electricitysupplylow?: number;
  electricityusagehigh?: string;
  electricityusagelow?: string;
  gasusage?: string;
  review?: Review;
  compare?: boolean;
}

export function getClassesByState(isOpen, isTopThree = false) {
  let wrapperClass = '';
  let toggleLabelMobile = 'Meer';
  let toggleLabelDestkop = 'Meer informatie';

  if (isTopThree) {
    toggleLabelMobile = 'Meer';
    toggleLabelDestkop = 'Meer info';
  }
  if (isOpen) {
    wrapperClass = 'open';
    toggleLabelMobile = 'Minder';
    toggleLabelDestkop = 'Minder informatie';
  }

  if (isOpen && isTopThree) {
    wrapperClass = 'open';
    toggleLabelMobile = 'Minder';
    toggleLabelDestkop = 'Minder info';
  }

  return { wrapperClass, toggleLabelDestkop, toggleLabelMobile };
}

const getRowTitle = (total, isAuction) => {
  return isAuction ? (
    <Div display="flex" justifyContent="space-between">
      <P>Totaalbedrag per jaar</P>
      <P>{formatPrice(total)}</P>
    </Div>
  ) : (
    'Totaal'
  );
};
const IconWithTooltip = withTooltip(IconV2);

const textWithoutParenthesis = (word) =>
  word
    ?.replace('(boven plafond)', '')
    .replace('(daltarief boven plafond)', '(daltarief)')
    .replace('(onder plafond)', '')
    .replace('(daltarief onder plafond)', '(daltarief)')
    .replace('(normaaltarief onder plafond)', '(normaaltarief)')
    .replace('(normaaltarief boven plafond)', '(normaaltarief)');

const cbFixedReturnCosts = (
  <>
    <P variant="footnoteRegular">
      Met vaste terugleveringskosten worden de kosten gedekt voor het
      terugleveren van je opgewekte stroom. De kosten bestaan uit een vast
      bedrag per dag waarvan de hoogte afhankelijk is van de hoeveelheid
      daadwerkelijk opgewekte stroom per jaar. Per schaal gelden er vaste
      terugleveringskosten. Let op: de kosten die je hier ziet zijn gebaseerd op
      een geschatte hoeveelheid opgewekte stroom. De definitieve kosten worden
      berekend op je jaar- of eindnota.
    </P>
    <P variant="footnoteRegular" mt={2}>
      De vaste terugleveringskosten gelden alleen als je zelf stroom opwekt en
      zullen van toepassing zijn zodra we teruglevering van elektriciteit op je
      aansluiting registreren. Ook als dit tijdens de looptijd van je contract
      is. Voor meer info over de schalen en bijbehorende kosten zie&nbsp;
      <Anchor
        style={{
          textDecoration: 'none',
          wordBreak: 'break-all',
        }}
        href={'https://www.consumentenbond.nl/zonnepanelen/terugleverkosten'}
        type={'website'}
        color={'main.1'}
      >
        Zonnepanelen | Terugleverkosten | Consumentenbond
      </Anchor>
    </P>
  </>
);

export const getTariffsSummary = (
  tariffSummary,
  groupRowsKey,
  Component,
  compStyles = {},
  breakdown,
) => {
  const rows =
    breakdown?.breakdown?.electricity?.delivery?.items?.deliveryTotal?.rows ||
    [];
  const items = tariffSummary.items.priceSummary.rows;
  const itemsToArray = Object.keys(items).map((el) => ({
    ...items[el].rows,
    key: el,
  }));

  const fixedDeliveryCostsObject = rows.find((row) =>
    row.delivery.includes('terugleveringskosten'),
  );

  const electricityItems = itemsToArray
    .filter((el) => el.key.startsWith('electricity') || el.electricity)
    .map(({ demand, supply, electricity, key }) => {
      // Based on business logic, these data from BE might be under supply OR demand based on Input data. As for now, supply has a higher prio than demand
      const demandOrSupply = supply || demand;

      const label = textWithoutParenthesis(demandOrSupply?.delivery);

      if (electricity?.delivery.includes('Vaste terugleveringskosten')) {
        const _scale = fixedDeliveryCostsObject?.scale;
        return {
          label: (
            <Div
              variant="detailsListHeading"
              css={{
                // minWidth: '230px',
                alignItems: 'flex-start',
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
              }}
            >
              <P>
                {label || electricity?.delivery}
                &nbsp;
                {_scale && (
                  <Span
                    css={{
                      display: 'inline-flex',
                    }}
                  >
                    <Span variant="captionRegularSmall">({_scale})</Span>
                    <IconWithTooltip
                      name="info"
                      size={14}
                      css={{
                        fill: '#BEC0C3',
                        marginRight: '0 !important',
                        marginLeft: 1,
                      }}
                      _tooltip={cbFixedReturnCosts}
                      cursor="pointer"
                    />
                  </Span>
                )}
              </P>
            </Div>
          ),
          value: demandOrSupply?.priceYear
            ? formatPrice(demandOrSupply?.priceYear, 5)
            : formatPrice(electricity?.priceYear, 2),
          key: key,
        };
      }

      return {
        label: label || electricity?.delivery,
        value: demandOrSupply?.priceYear
          ? formatPrice(demandOrSupply?.priceYear, 5)
          : formatPrice(electricity?.priceYear, 2),
        key: key,
        component: Component && label && (
          <Component text={demandOrSupply?.delivery} {...compStyles} />
        ),
      };
    });

  const gasItems = itemsToArray
    .filter((el) => el.key.startsWith('gas') || el.gas)
    .map(({ demand, key, gas }) => {
      // BE seems to not care on delivering the correct transformed data
      const label = textWithoutParenthesis(demand?.delivery);

      return {
        label: label || gas?.delivery,
        value: demand?.priceYear
          ? formatPrice(demand?.priceYear, 5)
          : formatPrice(gas?.priceYear, 2),
        key: key,
        component: Component && label && (
          <Component text={demand?.delivery} {...compStyles} />
        ),
      };
    });

  const group = [
    { title: 'Stroom', [groupRowsKey]: electricityItems },
    { title: 'Gas', [groupRowsKey]: gasItems },
  ].filter((el) => el[groupRowsKey].length > 0);

  return group;
};

export const handleEnergyData = (data, variant = '', compStyles) => {
  const { image, price, features, availableContracts = null } = data;
  const { breakdown, month, year } = price;
  const { discountIncluded } = year;
  const { tariffs, tariffSummary } = breakdown;
  const { electricity, gas } = tariffs;

  const newGroup = getTariffsSummary(
    tariffSummary,
    'rows',
    PriceCapCeillingLabel,
    compStyles,
    breakdown,
  );

  const logo = {
    alt: '',
    url: image,
  };

  let rates: { electricity: Field[]; gas: Field[] } = {
    electricity: [],
    gas: [],
  };

  const electricityRates = getElectricityRates(electricity);

  rates['electricity'] = [...electricityRates[0]];
  rates['gas'] = getGasRates(gas);

  const bb = breakdown.breakdown;

  for (const category in bb) {
    const categoryData = bb[category];

    if (Object.keys(categoryData).length) {
      const rows = categoryData['delivery']['items']['deliveryTotal']['rows'];

      for (const row of rows) {
        const { delivery, priceYear } = row;
        if (delivery === 'Vaste leveringskosten') {
          rates[category].push({
            label: `${delivery} per jaar`,
            value: formatPrice(priceYear),
          });
        }
      }
    }
  }

  rates['electricity'] = [...rates['electricity'], ...electricityRates[1]];

  let prices: Field[] = [];

  if (discountIncluded) {
    prices.push({
      label: 'Totaalbedrag per jaar',
      value: formatPrice(discountIncluded + year.total),
    });
    prices.push({
      label: 'Eenmalige korting per jaar',
      value: formatPrice(-discountIncluded),
    });
  }

  let groups: Group[] = [];

  if (rates['electricity'].length) {
    groups.push({
      title: 'Stroom',
      rows: rates['electricity'],
    });
  }

  if (rates['gas'].length) {
    groups.push({
      title: 'Gas',
      rows: rates['gas'],
    });
  }

  const isAuction = variant === 'auction';

  groups.push({
    title: getRowTitle(year.total, isAuction),
    rows: !isAuction
      ? [
          ...prices,
          {
            label: 'Kosten per jaar',
            value: `${formatPrice(year.total)}`,
          },
          {
            label: 'Kosten per maand',
            value: `${formatPrice(month.total)}`,
          },
        ]
      : [],
    disclaimer:
      'Deze kosten zijn incl. btw, netbeheerkosten en overheidsheffingen.',
  });

  const getTitle = (dataContent) => {
    const { name, label } = dataContent[1];
    return `${dataContent[1].value} ${
      name ?? label.toLowerCase()
    }, ${dataContent[0].value.toLowerCase()}`;
  };

  return {
    logo,
    title: getTitle(features),
    groups: [
      ...newGroup,
      {
        title: getRowTitle(year.total, isAuction),
        rows: !isAuction
          ? [
              ...prices,
              {
                label: 'Kosten per jaar',
                value: `${formatPrice(year.total)}`,
              },
              {
                label: 'Kosten per maand',
                value: `${formatPrice(month.total)}`,
              },
            ]
          : [],
        disclaimer:
          'Deze kosten zijn incl. btw, netbeheerkosten en overheidsheffingen.',
      },
    ],
    // groups,
    preview: `Totaal per maand: ${formatPrice(month.total)}`,
    amountOfAvailableContracts: availableContracts,
  };
};

export function onClick(
  interpreter,
  service,
  data,
  nextIndex,
  registerAnalyticsAddToCartEvent?,
  registerInPeregrineEvent?,
  api?,
) {
  const filters = interpreter
    .getSnapshot()
    ?.context.originalFields.find((field) => field.name === 'filters');

  if (registerAnalyticsAddToCartEvent) {
    registerAnalyticsAddToCartEvent(data, JSON.parse(filters?.value));
  }
  if (registerInPeregrineEvent) {
    const allFields = [
      ...(interpreter.getSnapshot().context.externalFields || []),
      ...interpreter.getSnapshot().context.fields,
    ];
    try {
      const graphApi = api as GraphComparisonApiService<GraphLegacyApiService>;
      const p1 = graphApi.getPeregrineUserHash(allFields);
      const p2 = graphApi.getPeregrinePartnerHash(allFields);
      const p3 = graphApi.getPeregrineActiveExperiments(allFields);

      Promise.all([p1, p2, p3]).then(
        ([userHash, partnerHash, activeExperiments]) => {
          const eventData = registerInPeregrineEvent(
            allFields,
            userHash,
            partnerHash,
            activeExperiments,
          );
          graphApi.sendPeregrineEvent(eventData);
        },
      );
    } catch (e) {
      console.log(e);
    }
  }

  service.send([
    {
      type: 'CHANGE',
      target: {
        name: 'selectedProduct',
        value: JSON.stringify(data) as any,
      },
    },
    {
      type: 'SET_FORM_FIELDS',
      name: interpreter.id,
      fields: [
        { name: 'selectedProduct', value: JSON.stringify(data) },
        { name: 'hasIncentive', value: data.incentive !== null },
      ],
    },
  ]);
  service.send([
    nextIndex ? { type: 'GOTO', index: nextIndex } : { type: 'SUBMIT' },
  ]);

  if (service.state?.value === 'completion') {
    scrollToTop();
  }

  return null;
}

export const parseProductFieldData = (productValue: any) =>
  (typeof productValue === 'string'
    ? JSON.parse(productValue)
    : productValue) ?? '';

interface ProsConsItem {
  title1: string;
  title2: string;
  text1: string;
  text2: string;
}

interface ProductProps {
  attributes: Attributes;
  bigProductFeature: {
    hideIfZero: boolean;
    label: string;
    value: number;
    variant: string;
  };
  discount: number;
  energy_label: EnergyLabel;
  features: any;
  id: string;
  image: string;
  initialValue: number;
  name: string;
  oneOffCostsAmount: number;
  policyPolicyDetails: Policy[];
  price: Price;
  priceLabel: string;
  productDescription: string;
  products: Partial<EnergyProduct>[];
  providerDescription: string;
  purchasableProduct: boolean;
  review?: Review;
  sustainabilityCommerceScore: string;
  sustainabilityInvestmentScore: string;
  sustainabilityScore: string;
  usps: Usps;
  total: number;
  type: string;
}

export function getProductProps(data, priceMode): ProductProps {
  const { type } = data;

  let obj: ProductProps = {
    attributes: {
      annual_account: {
        digital: '',
        mail: '',
      },
      edit_amount: {
        app: '',
        online: '',
        phone: '',
      },
      extraInfo: [],
      moveWindow: {
        maxDays: 0,
        minDays: 0,
      },
      meter_readings: {
        app: '',
        online: '',
        phone: '',
      },
      noReflectionPeriod: false,
      payment_method: {
        acceptgiro: '',
        automatic: '',
        iDeal: '',
      },
      postcoderoosregeling: '',
      prosAndCons: {
        pros: {
          title1: '',
          title2: '',
          text1: '',
          text2: '',
        },
        cons: {
          title1: '',
          title2: '',
          text1: '',
          text2: '',
        },
      },
      reviews: {
        bad_indicator: [],
        customer_score: '',
        expert_quote: '',
        good_indicator: [],
        helpdesk_score: '',
        rs_customer_service_total: '',
        rs_durability_total: '',
        rs_online_service_total: '',
        rs_payment_total: '',
        rs_price_understanding_total: '',
        rs_transfer_process_total: '',
        total_score: '',
      },
      sustainabilityScore: '',
      switchCountWeek: 0,
      switchWindow: {
        maxDays: 0,
        minDays: 0,
      },
      usage: {
        app: '',
        online: '',
        phone: '',
      },
    },
    bigProductFeature: {
      hideIfZero: false,
      label: '',
      value: 0,
      variant: 'priceMedium',
    },
    discount: 0,
    energy_label: {
      co2_per_kwh: 0,
      percentage_bio: 0,
      percentage_gas: 0,
      percentage_groen: 0,
      percentage_kern: 0,
      percentage_kolen: 0,
      percentage_overig: 0,
      percentage_water: 0,
      percentage_wind: 0,
      percentage_zon: 0,
      radioactief_afval_per_kwh: 0,
    },
    features: {},
    id: '',
    image: '',
    initialValue: 0,
    name: '',
    oneOffCostsAmount: 0,
    policyPolicyDetails: [],
    priceLabel: `per ${getLabel(priceMode)}`,
    productDescription: '',
    products: [],
    providerDescription: '',
    purchasableProduct: true,
    price: {
      breakdown: {},
      month: { discountIncluded: 0, total: 0 },
      year: { discountIncluded: 0, total: 0 },
    },
    review: undefined,
    sustainabilityCommerceScore: '',
    sustainabilityInvestmentScore: '',
    sustainabilityScore: '',
    usps: {},
    total: 0,
    type: '',
  };

  if (type === COMPARATOR_TYPE.ENERGY) {
    const {
      availability,
      energy_label,
      features,
      id,
      image,
      price,
      products,
      provider,
      review,
      usps,
    } = data;
    const {
      attributes = {},
      description: providerDescription,
      name,
    } = provider;

    const {
      sustainabilityScore = '',
      sustainabilityCommerceScore = '',
      sustainabilityInvestmentScore = '',
    } = attributes;
    const discount: number = price[priceMode].discountIncluded;

    obj['bigProductFeature'] = {
      ...obj['bigProductFeature'],
      hideIfZero: true,
      label: 'Besparing',
      value: discount,
    };
    obj = {
      ...obj,
      attributes,
      discount,
      energy_label,
      features,
      id,
      image,
      name,
      price,
      products,
      providerDescription,
      purchasableProduct:
        availability?.acceptance?.accepted &&
        availability?.acceptance?.reason !== 'noContractsAvailable',
      review,
      sustainabilityCommerceScore,
      sustainabilityInvestmentScore,
      sustainabilityScore,
      usps,
      total: price[priceMode].total,
      type,
    };
  } else {
    const {
      companyNameDescription: name,
      offerNumber,
      oneOffCostsAmount,
      productCode,
      packageDiscountInstallmentAmount: discount,
      policyPolicyDetails,
      productDescription,
      totalInstallmentAmount: total,
    } = data;

    obj['bigProductFeature']['label'] = 'Eigen risico';
    obj['id'] = `${productCode}-${offerNumber}`;
    obj['initialValue'] =
      discount && typeof total === 'number' ? discount + total : 0;
    obj = {
      ...obj,
      discount,
      name,
      oneOffCostsAmount,
      policyPolicyDetails,
      productDescription,
      total,
    };
  }

  return obj;
}

export function getProductsWithReviews(visibleProducts, reviews): IProduct[] {
  return visibleProducts.map((product) => ({
    ...product,
    review: reviews.find(({ providerId }) => {
      return (providerId || [''])
        .map(String)
        .includes(String(product.provider.id));
    }),
  }));
}
